import { useMemo, type Ref } from "react";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { LeftNavClosedIcon } from "@icons";
import { useSideNavContext } from "./SideNavProvider";
import styles from "./SideNavShowButton.module.scss";

/**
 * Brings a hidden nav back; place it at the far left of the page header.
 * Renders only while the nav is hidden, fading in once the nav has slid away.
 * Hiding the nav while focus is inside it moves focus here.
 */
export const SideNavShowButton = ({
    className,
    ref,
}: {
    className?: string;
    ref?: Ref<HTMLButtonElement>;
}) => {
    const { isHidden, isSuppressed, setHidden, showButtonRef } =
        useSideNavContext();
    const buttonRef = useMemo(
        () => mergeRefs([ref, showButtonRef]),
        [ref, showButtonRef],
    );

    if (!isHidden || isSuppressed) {
        return null;
    }

    return (
        <Button
            ref={buttonRef}
            variant="secondary"
            size="md"
            iconOnly
            aria-label="Show navigation"
            className={classNames(styles.showButton, className)}
            onClick={() => setHidden(false)}
        >
            <LeftNavClosedIcon />
        </Button>
    );
};
